import React, { useState } from "react";
import { useLang } from "../context/LangContext";
import PageHeader from "../components/site/PageHeader";
import { COMPANY, IMG } from "../lib/siteData";
import { submitContact } from "../lib/api";
import { Mail, MapPin, Globe, Clock, Phone, ArrowRight, CheckCircle2, FileDown } from "lucide-react";
import { toast, Toaster } from "sonner";

const Contact = () => {
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", country: "", subject: "", message: "" });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success(t("common.success"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-center" richColors />
      <PageHeader eyebrow={t("nav.contact")} title={t("contact.title")} subtitle={t("contact.subtitle")} image={IMG.controlRoom} />

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="overline mb-4">{t("contact.formTitle")}</div>
            <h2 className="section-title text-3xl sm:text-4xl mb-8">Tell us about your project.</h2>

            {done ? (
              <div className="bg-steel-50 border border-steel-200 p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" strokeWidth={1.4} />
                <h3 className="font-heading font-bold text-2xl text-ink mb-2">Message received</h3>
                <p className="text-ink-muted">{t("common.success")}</p>
                <button onClick={() => { setDone(false); setForm({ name: "", company: "", email: "", phone: "", country: "", subject: "", message: "" }); }} className="mt-6 inline-flex items-center gap-2 text-brand font-semibold text-sm uppercase tracking-[0.18em]">
                  Send another <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <CField label={t("contact.name")} name="name" value={form.name} onChange={onChange} required testId="contact-name" />
                  <CField label={t("contact.company")} name="company" value={form.company} onChange={onChange} testId="contact-company" />
                  <CField label={t("contact.email")} name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email" />
                  <CField label={t("contact.phone")} name="phone" value={form.phone} onChange={onChange} testId="contact-phone" />
                  <CField label={t("contact.country")} name="country" value={form.country} onChange={onChange} testId="contact-country" />
                  <CField label={t("contact.subject")} name="subject" value={form.subject} onChange={onChange} testId="contact-subject" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-soft mb-2">{t("contact.message")} *</label>
                  <textarea required name="message" rows={6} value={form.message} onChange={onChange} data-testid="contact-message" className="w-full bg-white border border-steel-300 px-4 py-3 text-sm focus:border-brand focus:outline-none resize-y" />
                </div>
                <button disabled={submitting} type="submit" data-testid="contact-submit" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white px-7 py-4 font-semibold text-sm uppercase tracking-[0.18em] transition-colors">
                  {submitting ? t("common.loading") : t("common.send")} <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5 lg:pl-6 lg:border-l lg:border-steel-200">
            <div className="overline mb-4">{t("contact.officeTitle")}</div>
            <h3 className="font-heading font-bold text-2xl text-ink mb-6">{COMPANY.name}</h3>
            <ul className="space-y-5 text-sm">
              <InfoItem icon={MapPin} label="Address">{COMPANY.address}</InfoItem>
              <InfoItem icon={Mail} label="Email"><a href={`mailto:${COMPANY.email}`} className="hover:text-brand" data-testid="contact-info-email">{COMPANY.email}</a></InfoItem>
              <InfoItem icon={Phone} label="Phone / WhatsApp"><a href={COMPANY.phoneHref} className="hover:text-brand" data-testid="contact-info-phone">{COMPANY.phone}</a></InfoItem>
              <InfoItem icon={Globe} label="Website"><a href={COMPANY.websiteHref} target="_blank" rel="noreferrer" className="hover:text-brand">{COMPANY.website}</a></InfoItem>
              <InfoItem icon={Clock} label="Working Hours">{t("contact.hours")}</InfoItem>
            </ul>

            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/profile.pdf`}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="contact-download-profile"
              className="mt-8 inline-flex items-center gap-2 bg-ink hover:bg-brand text-white px-6 py-3.5 font-semibold text-sm uppercase tracking-[0.18em] transition-colors"
            >
              <FileDown className="w-4 h-4" /> Download Company Profile
            </a>

            <div className="mt-8">
              <iframe
                title="Waves Technology FZCO — Dubai Silicon Oasis"
                src="https://www.google.com/maps?q=Dubai+Silicon+Oasis+DDP+Building+A1&output=embed"
                className="w-full h-72 border border-steel-200"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CField = ({ label, name, type = "text", value, onChange, required, testId }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-soft mb-2">{label}{required && " *"}</label>
    <input type={type} name={name} value={value} onChange={onChange} required={required} data-testid={testId}
      className="w-full bg-white border border-steel-300 px-4 py-3 text-sm focus:border-brand focus:outline-none" />
  </div>
);

const InfoItem = ({ icon: Icon, label, children }) => (
  <li className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-brand mt-0.5 shrink-0" strokeWidth={1.5} />
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-steel-500 mb-1">{label}</div>
      <div className="text-ink-soft">{children}</div>
    </div>
  </li>
);

export default Contact;
